import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { FormattedMessage } from 'react-intl';
import platform from '@/utils/platform';
import Theme from '@/connectHOCs/Theme';
import {
  getCurrentAppVersion,
  getSoundPreference,
  subscribeNotificationPreferences,
  unsubscribeNotificationPreferences,
  getUserOnlineNotification,
  getNotificationPreferences,
  getUserInfo,
  publishGenericEvent,
  setCurrentSession,
} from '@/utils/SessionUtils';
import {
  showAddNewTeam,
  showReportBugPopup,
  showReferSomeoneModal,
  exit,
  showAboutUs,
} from '@/utils/ShellUtils';
import currentSessionHOC from '@/connectHOCs/Sessions/currentSession';
import bridge from '@/utils/bridge';
import cssStyles from './Dropdown.css';
import TeamInfo from './TeamInfo';
import Divider from './Divider';
import DoNotDisturb from './DoNotDisturb';
import EditProfile from './EditProfile';
import EditStatus from './EditStatus';
import ManageApps from './ManageApps';
import ManageTeam from './ManageTeam';
import SignOut from './SignOut';
import Notifications from './Notifications';
import ToggleButton from '../common/ToggleButton/ToggleButton';
import AppMenuTeamSwitcher from '../teamSwitcher/AppMenuTeamSwitcher';

class Dropdown extends Component {
  static propTypes = {
    currentSession: PropTypes.object.isRequired,
    currentSessionId: PropTypes.string.isRequired,
    shellLayout: PropTypes.object,
    onBlur: PropTypes.func,
    currentTheme: PropTypes.string,
    toggleTheme: PropTypes.func,
  };

  static defaultProps = {
    shellLayout: false,
    onBlur: () => {},
    currentTheme: 'light',
    toggleTheme: () => {},
  };

  constructor(props) {
    super(props);
    this.state = {
      notificationPreferences: {
        userOnlineNotification: false,
      },
      soundPreference: {},
      appVersion: '0.0.0.0',
      showReferLink: false,
    };

    this.dropdownRef = React.createRef();
  }

  componentDidMount() {
    const { currentSessionId, currentSession } = this.props;
    this.dropdownRef.current.focus();
    platform.isDesktopClient().then(isDesktopClient => {
      this.setState({ showQuit: isDesktopClient });
    });
    getCurrentAppVersion().then(response => {
      this.setState({ appVersion: response });
    });
    getSoundPreference().then(response => {
      this.setState({ soundPreference: response });
    });
    this.getSessionValues(currentSessionId);
    this.showReferLinkInMenu(currentSession);
    subscribeNotificationPreferences(currentSessionId, (type, value) => {
      this.setState(({ notificationPreferences }) => ({
        notificationPreferences: {
          ...notificationPreferences,
          [type]: value,
        },
      }));
    });
  }

  componentWillUnmount() {
    unsubscribeNotificationPreferences();
  }

  getSessionValues(sessionId) {
    getUserOnlineNotification(sessionId).then(response => {
      this.setState(({ notificationPreferences }) => ({
        notificationPreferences: {
          ...notificationPreferences,
          userOnlineNotification: response,
        },
      }));
    });
    getNotificationPreferences(sessionId).then(response => {
      this.setState(({ notificationPreferences }) => ({
        notificationPreferences: {
          ...notificationPreferences,
          ...response,
        },
      }));
    });
  }

  openKeyboardShortcutsModal = () => {
    bridge.publish('odara-keyboard-shortcuts-modal');
  };

  showReferLinkInMenu = session => {
    const { guid } = session.sessionInfo.teamInfo;
    const token = session.sessionInfo.metaToken;
    const ONE_DAY = 24 * 60 * 60 * 1000;

    getUserInfo(token, guid).then(res => {
      const createdOn = res.createdOn || 0;
      if (Date.now() - createdOn > 60 * ONE_DAY) {
        const payload = {
          ownerGuid: guid,
          isReferralPresent: true,
        };
        publishGenericEvent('referral_shown_in_menu', payload);
        this.setState({
          showReferLink: true,
        });
      }
    });
  };

  // openAboutFlockModal = version => {
  //   bridge.publish('odara-about-flock-modal', [version]);
  // };
  //
  onBlur = event => {
    const target = event.currentTarget;
    setTimeout(() => {
      if (target.contains(document.activeElement)) {
        return;
      }
      const { onBlur } = this.props;
      if (onBlur) {
        onBlur();
      }
    }, 0);
  };

  toggle = () => {
    const { toggleTheme } = this.props;
    toggleTheme();
  };

  render() {
    const {
      notificationPreferences,
      soundPreference,
      appVersion,
      showReferLink,
      showQuit,
    } = this.state;
    const { shellLayout, currentTheme: themeName, currentSession } = this.props;

    if (!currentSession) return null;

    const currentSessionId = currentSession.id;

    const { collapsed } = shellLayout.outerLeft;
    const currentVersion =
      appVersion !== '##APP_VERSION##' ? appVersion : ': Dev Mode';
    const isDarkTheme = themeName === 'dark';
    const dropdownListClassName = classNames(cssStyles.dropdown_list, {
      [cssStyles.dropdown_list_collapsed]: collapsed,
      [cssStyles.dropdown_list_dark]: isDarkTheme,
    });
    const listItemClassName = classNames(cssStyles.dropdown_list_item);
    const currentSessionInfo = currentSession.sessionInfo;

    return (
      <React.Fragment>
        <ReactTooltip className={cssStyles.tooltip} />
        <div
          className={dropdownListClassName}
          ref={this.dropdownRef}
          onBlur={this.onBlur}
          tabIndex={-1}
        >
          <TeamInfo session={currentSession} shellLayout={shellLayout} />
          {collapsed && (
            <div
              role='menuitem'
              onKeyDown={() => {}}
              tabIndex='-1'
              onClick={e => e.stopPropagation()}
            >
              <AppMenuTeamSwitcher
                currentSessionId={currentSessionId}
                isHorizontal
                onTeamSwitch={setCurrentSession}
                isMenuTeamSwitcher
              />
            </div>
          )}
          <Divider />
          <DoNotDisturb
            sessionId={currentSessionId}
            notificationPreferences={notificationPreferences}
          />
          <Divider />
          <EditProfile session={currentSession} />
          <EditStatus session={currentSession} />
          <Divider />
          <Notifications
            sessionId={currentSessionId}
            soundPreference={soundPreference}
            notificationPreferences={notificationPreferences}
          />
          <Divider />
          <div
            role='menuitem'
            onKeyDown={() => {}}
            tabIndex='-1'
            className={listItemClassName}
            onClick={() =>
              showAddNewTeam(
                currentSession.accountEmail,
                currentSessionInfo.teamInfo.teamId
              )
            }
          >
            <FormattedMessage id='add_team' />
          </div>
          {currentSession.owner.isAdmin ? <ManageTeam /> : ''}
          <ManageApps sessionId={currentSessionId} />
          <Divider />
          <SignOut />
          <Divider />
          <div
            role='menuitem'
            onKeyDown={() => {}}
            tabIndex='-1'
            className={listItemClassName}
            onClick={() => this.toggle()}
          >
            <FormattedMessage id='dark_theme' />
            <ToggleButton isActive={isDarkTheme} />
          </div>
          <div
            role='menuitem'
            onKeyDown={() => {}}
            tabIndex='-1'
            className={listItemClassName}
            onClick={this.openKeyboardShortcutsModal}
            // onClick={showKeyboardShortcuts}
          >
            <FormattedMessage id='keyboard_shortcuts' />
          </div>
          <div
            role='menuitem'
            onKeyDown={() => {}}
            tabIndex='-1'
            className={listItemClassName}
            onClick={showReportBugPopup}
          >
            <FormattedMessage id='contact_support' />
          </div>
          {showReferLink && (
            <div
              role='menuitem'
              onKeyDown={() => {}}
              tabIndex='-1'
              className={listItemClassName}
              onClick={() => showReferSomeoneModal(currentSession)}
            >
              <FormattedMessage id='refer_someone' />
            </div>
          )}
          {showQuit && (
            <div
              role='menuitem'
              onKeyDown={() => {}}
              tabIndex='-1'
              className={listItemClassName}
              onClick={exit}
            >
              <FormattedMessage id='quit_flock' />
            </div>
          )}
          <div
            role='menuitem'
            onKeyDown={() => {}}
            tabIndex='-1'
            className={listItemClassName}
            onClick={showAboutUs}
          >
            Version {currentVersion}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default currentSessionHOC(Theme(Dropdown));



// WEBPACK FOOTER //
// ./src/components/appMenu/Dropdown.js